import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { kpiData, testimonials, companyLogos } from '../data/mockData';
import { ArrowRight, BarChart3, TrendingUp, Target, CheckCircle, Play } from 'lucide-react';
import { Button } from '../components/ui/button';

const PerformanceInsightsPage = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full filter blur-3xl" />
            <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-teal-500/10 rounded-full filter blur-3xl" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Performance Insights:
                <span className="block bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                  Unique KPI Data, Analytics & Insights
                </span>
              </h1>
              
              {/* Animated up arrows */}
              <div className="flex justify-center gap-2 mb-8">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="w-8 h-12 flex items-center justify-center"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    <TrendingUp className="w-6 h-6 text-emerald-400 animate-bounce" style={{ animationDelay: `${i * 0.2}s` }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Logo Carousel */}
            <div className="flex justify-center gap-12 mt-12 opacity-60">
              {companyLogos.slice(0, 6).map((company, index) => (
                <img
                  key={index}
                  src={company.logo}
                  alt={company.name}
                  className="h-8 grayscale brightness-200"
                />
              ))}
            </div>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h4 className="text-emerald-600 font-medium mb-2">Better Understand your Organization. Know your Performance Value.</h4>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Transform How CXOs Monitor & Improve Performance
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  <strong>Performance Insights transforms how CXOs prepare, analyze, and improve performance—at scale.</strong> Built on a foundation of trusted, verified, and timely organizational intelligence, it equips executives with ready-to-use insights, strategic content, and precision-aligned metrics that resonate with stakeholders.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Embedded learning modules explain the insights—what they are, why they are important, and how to use them in strategic decisions.
                </p>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600"
                  alt="Analytics Dashboard"
                  className="rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Different */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8">Why Performance Insights is Different</h2>
                
                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <ArrowRight className="w-5 h-5 text-emerald-600" />
                      KPI Insights, Reimagined for CXOs
                    </h3>
                    <p className="text-gray-600 pl-7">
                      Performance Insights delivers best-of-breed organizational intelligence—leveraging structured data, analytics, and verified AI. Unlike generic tools, our approach ensures outputs are accurate, relevant, timely, and enterprise-grade.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <ArrowRight className="w-5 h-5 text-emerald-600" />
                      AI-Powered, Executive-Ready Prompts
                    </h3>
                    <p className="text-gray-600 pl-7">
                      Generate accurate executive-facing content—reports, presentations, strategic plans, and more—in seconds. Grounded in your organization's actual performance data.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <ArrowRight className="w-5 h-5 text-emerald-600" />
                      Benchmarking & Context
                    </h3>
                    <p className="text-gray-600 pl-7">
                      Instantly compare performance against industry averages and top performers, surface sector insights, and align your narrative with organizational goals using verified data.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <img
                  src="https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=600"
                  alt="Business Analytics"
                  className="rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features List */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Built for CXOs. Trusted by Industry Leaders.</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                'Unique, Actionable KPI Insights and Benchmarks',
                'Financial Impact Calculations per KPI',
                'Embedded Learning Modules',
                'Custom Strategy and Performance Mapping',
                'UX built for executive-level understanding',
                'Integration with Enterprise Systems',
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* KPI Preview */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Smarter Performance. Measurable Impact.</h2>
              <p className="text-gray-400">Sample KPI benchmarks for a Technology sector organization</p>
            </div>

            <div className="bg-gray-800 rounded-2xl p-6 overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-700">
                    <th className="pb-4 font-medium">KPI</th>
                    <th className="pb-4 font-medium text-center">Your Value</th>
                    <th className="pb-4 font-medium text-center">Industry Avg</th>
                    <th className="pb-4 font-medium text-center">Top Performer</th>
                    <th className="pb-4 font-medium text-right">1% Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {kpiData.slice(0, 5).map((kpi, index) => (
                    <tr key={index} className="border-b border-gray-700/50">
                      <td className="py-4 text-white font-medium">{kpi.name}</td>
                      <td className="py-4 text-center">
                        <span className="text-emerald-400 font-semibold">
                          {typeof kpi.yourValue === 'number' && kpi.yourValue > 1000 
                            ? `$${(kpi.yourValue / 1000).toFixed(0)}K` 
                            : kpi.yourValue}{kpi.unit !== '$/employee' && kpi.unit !== 'days' ? kpi.unit : ''}
                        </span>
                      </td>
                      <td className="py-4 text-center text-gray-400">
                        {typeof kpi.industryAvg === 'number' && kpi.industryAvg > 1000 
                          ? `$${(kpi.industryAvg / 1000).toFixed(0)}K` 
                          : kpi.industryAvg}{kpi.unit !== '$/employee' && kpi.unit !== 'days' ? kpi.unit : ''}
                      </td>
                      <td className="py-4 text-center text-yellow-400">
                        {typeof kpi.topPerformer === 'number' && kpi.topPerformer > 1000 
                          ? `$${(kpi.topPerformer / 1000).toFixed(0)}K` 
                          : kpi.topPerformer}{kpi.unit !== '$/employee' && kpi.unit !== 'days' ? kpi.unit : ''}
                      </td>
                      <td className="py-4 text-right text-emerald-400 font-medium">
                        ${(kpi.impactPer1Percent / 1000000).toFixed(1)}M
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-center mt-8">
              <Link to="/dashboard">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                  Get Your FREE Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Where Insights Meet Impact: According to Our Clients
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                  <p className="text-gray-600 italic mb-6">"{testimonial.quote}"</p>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.author}</div>
                    <div className="text-emerald-600 text-sm">{testimonial.company}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PerformanceInsightsPage;
